// IdentifierEditor — chip list of CAS / EC / FEMA / PubChem / InChIKey /
// Internal identifiers + an inline add form. Ported from
// selectors-sidebar.jsx (lines 242-312).

import * as React from "react";
import { IDENTIFIER_KINDS } from "./constants";
import type { IdentifierEntry } from "./_types";

const XMark = ({ size = 9 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const Plus = ({ size = 9 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

type Props = {
  identifiers: IdentifierEntry[];
  onAdd: (entry: { kind: string; value: string }) => void;
  onRemove: (idx: number) => void;
};

export function IdentifierEditor({ identifiers, onAdd, onRemove }: Props) {
  const [adding, setAdding] = React.useState(false);
  const [kind, setKind] = React.useState<string>("FEMA");
  const [val, setVal] = React.useState("");
  const used = identifiers.map((i) => i.kind.toUpperCase());

  return (
    <div className="vstack" style={{ gap: 8 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        {identifiers.map((id, i) => {
          const kindU = id.kind.toUpperCase();
          const isCas = kindU === "CAS";
          return (
            <span
              key={id.id ?? i}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 4px 3px 8px",
                borderRadius: 4,
                background: isCas ? "var(--accent-soft)" : "var(--bg-sunk)",
                color: isCas ? "var(--accent-ink)" : "var(--ink-2)",
                border: `1px solid ${isCas ? "var(--accent-line)" : "var(--line)"}`,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              <span style={{ opacity: 0.7, fontWeight: 700 }}>{kindU}</span>
              <span style={{ fontWeight: 500 }}>{id.value}</span>
              <button
                onClick={() => onRemove(i)}
                style={{
                  display: "inline-flex",
                  padding: 2,
                  border: "none",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                }}
                aria-label={`Remove ${kindU} ${id.value}`}
              >
                <XMark size={9} />
              </button>
            </span>
          );
        })}
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 9px",
              fontSize: 11,
              background: "transparent",
              border: "1px dashed var(--line-strong)",
              borderRadius: 4,
              color: "var(--ink-3)",
              cursor: "pointer",
            }}
          >
            <Plus size={9} />
            Tag identifier
          </button>
        )}
      </div>
      {adding && (
        <div
          className="hstack"
          style={{
            gap: 6,
            padding: 8,
            background: "var(--bg-sunk)",
            border: "1px solid var(--line)",
            borderRadius: 6,
          }}
        >
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            style={{
              padding: "5px 7px",
              fontSize: 11,
              fontFamily: "var(--font-mono)",
              border: "1px solid var(--line)",
              borderRadius: 4,
              background: "var(--panel)",
            }}
          >
            {IDENTIFIER_KINDS.map((k) => (
              <option key={k} value={k} disabled={used.includes(k.toUpperCase())}>
                {k}
              </option>
            ))}
          </select>
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="e.g. 2427 or 203-306-4"
            style={{
              flex: 1,
              padding: "5px 9px",
              fontSize: 12,
              fontFamily: "var(--font-mono)",
              border: "1px solid var(--line)",
              borderRadius: 4,
              background: "var(--panel)",
              outline: "none",
            }}
          />
          <button
            className="btn xs primary"
            disabled={!val.trim()}
            style={{ opacity: val.trim() ? 1 : 0.4 }}
            onClick={() => {
              onAdd({ kind, value: val.trim() });
              setVal("");
              setAdding(false);
            }}
          >
            Add
          </button>
          <button
            className="btn xs"
            onClick={() => {
              setAdding(false);
              setVal("");
            }}
            aria-label="Cancel add identifier"
          >
            <XMark size={10} />
          </button>
        </div>
      )}
    </div>
  );
}
